/**
 * useWaglRating Hook
 * 
 * Custom React hook for fetching WAGL ratings from the on-demand rating API.
 * Provides caching, loading states, and error handling.
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../services/api';

// Simple cache for rating data
const ratingCache = new Map();
const CACHE_TTL = 1; // 5 minutes

/**
 * Hook for fetching a single user's current WAGL rating
 * @param {string} userId - User ID to fetch rating for
 * @param {Object} options - Configuration options
 * @returns {Object} Hook state and functions
 */
export function useWaglRating(userId, options = {}) {
  const { 
    autoFetch = true, 
    includeHistory = false,
    asOfDate = null 
  } = options;

  const [rating, setRating] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const { get } = useApi();

  const fetchRating = useCallback(async (forceRefresh = false) => {
    if (!userId) {
      setError('User ID is required');
      return;
    }

    const cacheKey = `${userId}:${asOfDate?.getTime() || 'current'}:${includeHistory}`;
    
    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = ratingCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
        setRating(cached.data);
        setLastUpdated(cached.data.lastUpdated);
        setLoading(false);
        setError(null);
        return cached.data;
      }
    }

    setLoading(true);
    setError(null);

    try {
      let url = `/rating/user/${userId}`;
      if (includeHistory) {
        url += '/history';
      }

      const params = new URLSearchParams();
      if (asOfDate) {
        params.append('asOfDate', asOfDate.toISOString());
      }
      if (includeHistory) {
        params.append('includeSteps', 'false'); // Don't include debug steps by default
      }

      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await get(url);
      
      if (response.success) {
        const ratingData = {
          ...response.data,
          // Provide consistent interface
          waglRating: response.data.currentRating,
          // Include delta calculation if we have history
          waglRatingDelta: response.data.ratingHistory?.length > 0
            ? response.data.ratingHistory[response.data.ratingHistory.length - 1].newRating -
              response.data.ratingHistory[response.data.ratingHistory.length - 1].previousRating
            : 0
        };

        // Cache the result
        ratingCache.set(cacheKey, {
          data: ratingData,
          timestamp: Date.now()
        });

        setRating(ratingData);
        setLastUpdated(ratingData.lastUpdated);
        setError(null);
        return ratingData;
      } else {
        throw new Error(response.message || 'Failed to fetch rating');
      }
    } catch (err) {
      console.error('Error fetching WAGL rating:', err);
      setError(err.message || 'Failed to fetch rating');
      
      // Return default rating on error
      const defaultRating = {
        userId,
        currentRating: 50,
        waglRating: 50,
        gamesPlayed: 0,
        waglRatingDelta: 0,
        error: err.message
      };
      setRating(defaultRating);
      return defaultRating;
    } finally {
      setLoading(false);
    }
  }, [userId, get, asOfDate, includeHistory]);

  const refetch = useCallback(() => {
    return fetchRating(true);
  }, [fetchRating]);

  // Auto-fetch on mount and when dependencies change
  useEffect(() => {
    if (autoFetch && userId) {
      fetchRating();
    }
  }, [fetchRating, autoFetch, userId]);

  return {
    rating,
    loading,
    error,
    lastUpdated,
    refetch,
    fetchRating
  };
}

/**
 * Hook for fetching WAGL ratings for multiple users (leaderboard)
 * @param {Object} options - Configuration options
 * @returns {Object} Hook state and functions
 */
export function useWaglLeaderboard(options = {}) {
  const { 
    autoFetch = true, 
    limit = 50 
  } = options;

  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  const { get } = useApi();

  const fetchLeaderboard = useCallback(async (forceRefresh = false) => {
    const cacheKey = `leaderboard:${limit}`;
    
    // Check cache first unless forcing refresh
    if (!forceRefresh) {
      const cached = ratingCache.get(cacheKey);
      if (cached && (Date.now() - cached.timestamp) < CACHE_TTL) {
        setLeaderboard(cached.data.leaderboard);
        setTotalUsers(cached.data.totalUsers);
        setLastUpdated(new Date(cached.timestamp));
        setLoading(false);
        setError(null);
        return cached.data;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const url = `/rating/leaderboard?limit=${limit}`;
      const response = await get(url);
      
      if (response.success) {
        const leaderboardData = {
          leaderboard: response.data.leaderboard.map(user => ({
            ...user,
            waglRating: user.currentRating, // Provide consistent interface
            waglScore: user.currentRating   // Legacy compatibility
          })),
          totalUsers: response.data.totalUsers
        };

        // Cache the result
        ratingCache.set(cacheKey, {
          data: leaderboardData,
          timestamp: Date.now()
        });

        setLeaderboard(leaderboardData.leaderboard);
        setTotalUsers(leaderboardData.totalUsers);
        setLastUpdated(new Date());
        setError(null);
        return leaderboardData;
      } else {
        throw new Error(response.message || 'Failed to fetch leaderboard');
      }
    } catch (err) {
      console.error('Error fetching WAGL leaderboard:', err);
      setError(err.message || 'Failed to fetch leaderboard');
      setLeaderboard([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  }, [get, limit]);

  const refetch = useCallback(() => {
    return fetchLeaderboard(true);
  }, [fetchLeaderboard]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchLeaderboard();
    }
  }, [fetchLeaderboard, autoFetch]);

  return {
    leaderboard,
    loading,
    error,
    totalUsers,
    lastUpdated,
    refetch,
    fetchLeaderboard
  };
}

/**
 * Utility function to clear rating cache
 */
export function clearRatingCache() {
  ratingCache.clear();
}

/**
 * Utility function to get cache stats (for debugging)
 */
export function getRatingCacheStats() {
  return {
    size: ratingCache.size,
    keys: Array.from(ratingCache.keys()),
    ttl: CACHE_TTL
  };
}